import { useRef, useState } from 'react'
import { ApiRequestError } from '../../../lib/api'
import { useOnboardingV2Store } from '../../../stores/onboardingV2Store'
import OnboardingLayout from './OnboardingLayout'
import PrimaryButton from './PrimaryButton'
import { INPUT_CLASS, LABEL_CLASS } from './formStyles'
import { stepsForMode } from './steps'

const MAX_SOURCES = 5
const MIN_TEXT = 10

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function sourceMeta(src: { word_count: number; size_bytes: number | null }): string {
  const words = `${src.word_count.toLocaleString()} ${src.word_count === 1 ? 'word' : 'words'}`
  return src.size_bytes != null ? `${formatBytes(src.size_bytes)} · ${words}` : words
}

export default function UploadScreen() {
  const capabilityMode = useOnboardingV2Store((s) => s.state?.capability_mode ?? 'managed')
  const sources = useOnboardingV2Store((s) => s.sources)
  const stageFile = useOnboardingV2Store((s) => s.stageFile)
  const stageText = useOnboardingV2Store((s) => s.stageText)
  const removeSource = useOnboardingV2Store((s) => s.removeSource)
  const process = useOnboardingV2Store((s) => s.process)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [dragging, setDragging] = useState(false)

  const atCap = sources.length >= MAX_SOURCES
  const used = sources.length

  const run = async (fn: () => Promise<void>) => {
    setError(null)
    setBusy(true)
    try {
      await fn()
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.error.message : 'Something went wrong.')
    } finally {
      setBusy(false)
    }
  }

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return
    const file = files[0]
    await run(() => stageFile(file))
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleAddText = async () => {
    const trimmed = text.trim()
    if (trimmed.length < MIN_TEXT) return
    await run(async () => {
      await stageText(trimmed)
      setText('')
    })
  }

  const handleProcess = async () => {
    setError(null)
    setProcessing(true)
    try {
      await process()
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.error.message : 'Something went wrong.')
      setProcessing(false)
    }
  }

  return (
    <OnboardingLayout steps={stepsForMode(capabilityMode)} activeKey="upload">
      <div className="mx-auto w-full max-w-[600px]">
        <header className="mb-2 text-center">
          <h1 className="font-serif text-[28px] font-semibold text-ob-ink md:text-3xl">
            Add your career material
          </h1>
          <p className="mt-2 font-body text-base text-ob-secondary">
            Upload CVs or paste text — résumés, LinkedIn summaries, notes. We'll read it and pull
            out your professional proof.
          </p>
        </header>

        <p className="mb-6 text-center font-label text-[11px] uppercase tracking-wider text-ob-secondary">
          {used} of {MAX_SOURCES} sources used
        </p>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* File upload */}
          <div>
            <span className={LABEL_CLASS}>Upload files</span>
            <div
              onDragOver={(e) => {
                e.preventDefault()
                if (!atCap && !processing && !busy) setDragging(true)
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault()
                setDragging(false)
                if (!atCap && !processing && !busy) void handleFiles(e.dataTransfer.files)
              }}
              className={[
                'flex min-h-[140px] flex-col items-center justify-center gap-3 border border-dashed p-6 text-center',
                dragging ? 'border-ob-ink bg-ob-ink/5' : 'border-ob-secondary/40 bg-ob-surface-card',
                atCap || processing || busy ? 'opacity-50' : '',
              ].join(' ')}
            >
              <span className="material-symbols-outlined text-ob-secondary" aria-hidden="true">
                upload_file
              </span>
              <p className="font-body text-sm text-ob-secondary">
                <span className="hidden md:inline">Drag a file here, or </span>choose a file
              </p>
              <button
                type="button"
                disabled={atCap || busy || processing}
                onClick={() => fileInputRef.current?.click()}
                className="border border-ob-ink px-4 py-2 font-body text-sm font-semibold text-ob-ink transition-colors hover:bg-ob-ink/5 disabled:opacity-50"
              >
                Choose file
              </button>
              <p className="font-body text-xs text-ob-secondary/70">PDF, DOCX, or TXT · up to 10 MB</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx,.txt"
                className="hidden"
                onChange={(e) => void handleFiles(e.target.files)}
              />
            </div>
          </div>

          {/* Text paste */}
          <div>
            <label htmlFor="ob-text" className={LABEL_CLASS}>
              Or paste text
            </label>
            <textarea
              id="ob-text"
              value={text}
              disabled={atCap || busy || processing}
              onChange={(e) => setText(e.target.value)}
              rows={4}
              placeholder="Paste a résumé, a LinkedIn summary, or notes about what you've done…"
              className={`${INPUT_CLASS} min-h-[140px] resize-y md:min-h-[164px]`}
            />
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                disabled={atCap || busy || processing || text.trim().length < MIN_TEXT}
                onClick={() => void handleAddText()}
                className="border border-ob-ink px-4 py-2 font-body text-sm font-semibold text-ob-ink transition-colors hover:bg-ob-ink/5 disabled:opacity-50"
              >
                Add text
              </button>
            </div>
          </div>
        </div>

        {atCap && (
          <p className="mt-4 text-center font-body text-xs text-ob-secondary">
            You've added the maximum of {MAX_SOURCES} sources. Remove one to add another — you can
            always add more later in your Library.
          </p>
        )}

        {error && (
          <p className="mt-4 text-center font-body text-sm text-ob-error" role="alert">
            {error}
          </p>
        )}

        {/* Staged source list */}
        {sources.length > 0 && (
          <ul className="mt-8 divide-y divide-ob-secondary/20 border-y border-ob-secondary/20">
            {sources.map((src) => (
              <li key={src.source_id} className="flex items-center justify-between gap-3 py-3">
                <div className="flex min-w-0 items-center gap-3">
                  <span className="material-symbols-outlined text-[18px] text-ob-secondary" aria-hidden="true">
                    {src.source_type === 'file' ? 'description' : 'notes'}
                  </span>
                  <div className="min-w-0">
                    <span className="block truncate font-body text-sm text-ob-on-surface">
                      {src.source_label}
                    </span>
                    <span className="block font-body text-xs text-ob-secondary/70">
                      {sourceMeta(src)}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  disabled={busy || processing}
                  onClick={() => void run(() => removeSource(src.source_id))}
                  aria-label={`Remove ${src.source_label}`}
                  className="font-body text-sm text-ob-secondary transition-colors hover:text-ob-error disabled:opacity-50"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-10">
          <PrimaryButton
            className="w-full"
            disabled={sources.length === 0 || processing || busy}
            onClick={() => void handleProcess()}
          >
            {processing ? 'Processing…' : 'Process my CVs'}
          </PrimaryButton>
        </div>
      </div>
    </OnboardingLayout>
  )
}
